import { Color, ScaleType } from '@swimlane/ngx-charts';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { HelpersService } from '@app/shared/services/helpers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { DateTime } from 'luxon';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { OtpService } from '@app/shared/services/otp.service';
import { StateService, TimeBucketInfoInterface } from '@app/shared/services/state.service';
import * as _ from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import {
  AggregationType,
  ExportJobRequest,
  ExportJobResponse,
  ExportJobStatus,
  ShipmentAggregateResponse,
  TrendDirection,
} from '../../otp.interfaces';
import { AccountTypesForOTP, ShippingProviders } from '@app/shared/constants';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';

@Component({
  standalone: false,
  selector: 'app-otp-couriers',
  templateUrl: './otp-couriers.component.html',
  styleUrls: ['./otp-couriers.component.scss'],
})
export class OtpCouriersComponent implements OnInit, OnDestroy {
  public timeBucket$ = this.stateService.timeBucketInfo$;
  private timeBucketSubscription: Subscription;
  public courierFilter$ = this.stateService.courierFilter$;
  private courierFilterSubscription: Subscription;
  public adminUserInfo$: Observable<any> = this.stateService.adminUserInfo$;
  private adminUserInfoSubscription: Subscription;
  public canViewOtp: boolean = false;
  public currentBucket: string;
  public currentHistory: number;
  public accountType: AccountTypesForOTP;
  public courierFilter: ShippingProviders[];
  public user: AdminUser;

  public form: FormGroup | undefined;
  public couriersDropdown = [];
  public selectedCouriers: string[] = [];

  public loading: boolean = true;
  public currentPage: number = 0;
  public itemsPerPage: number = 5;
  public itemsPerPageOptions: number[] = [5, 10, 25];

  public daterange = {
    start: DateTime.now().minus({ day: 7 }).toJSDate(),
    end: DateTime.now().endOf('day').toJSDate(),
  };
  public range = new FormGroup({
    start: new FormControl<Date | null>(this.daterange.start),
    end: new FormControl<Date | null>(this.daterange.end),
  });

  public couriersSetup: boolean = false;
  public allServiceMetrics = [];
  public bucketData: any;

  public courierDelayDistribution: any[] = [];
  public courierDelayPercentageData: any[] = [];
  public selectedCourier: string = null;
  public showAsPercentage: boolean = false;
  public selectedCourierTrendData: any[] = [];

  // Performance Summary
  public totalShipments: number = 0;
  public onTimeRate: number = 0;
  public averageDelay: number = 0;
  // Guaranteed Service Metrics
  public gTotalShipments: number = 1240;
  public gOnTimeRate: number = 86;
  public gOnTimeCount: number = 1066;
  public gAverageDelay: number = 2.3;

  // Claims data
  public hasClaimsData: boolean = false;
  public totalClaims: number = 0;
  public overallClaimRate: number = 0;
  public claimRateTrend: TrendDirection = TrendDirection.STABLE;
  public lateDeliveryClaims: number = 0;
  public lateDeliveryClaimRate: number = 0;
  public courierClaimsData: any[] = [];
  public claimsByGradeData: any[] = [];
  public claimRateByServiceType: any[] = [];
  public claimTrendData: any[] = [];

  // Donut chart data
  public gradeDistribution = [
    { name: 'Grade A', value: 0 },
    { name: 'Grade B', value: 0 },
    { name: 'Grade C', value: 0 },
    { name: 'Grade D', value: 0 },
    { name: 'Grade F', value: 0 },
  ];

  public gradeDistributionTrends: any[] = [];

  // Bar chart data
  public serviceBreakdown = [];
  public courierComparison = [];
  public courierClaimRates = [];

  // Color schemes
  public claimsColorScheme: Color = {
    name: 'claimsScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#FF9800', '#E91E63', '#9C27B0', '#3F51B5', '#2196F3'],
  };

  public defaultColorScheme: Color = {
    name: 'defaultScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#5AA454', '#A10A28', '#C7B42C', '#17a2b8', '#6f42c1'],
  };

  public performanceColorScheme: Color = {
    name: 'performanceScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#5AA454', '#A10A28', '#C7B42C', '#17a2b8', '#6f42c1'],
  };

  public delayColorScheme: Color = {
    name: 'delayScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: [
      '#2E7D32', // < 1 hour (dark green)
      '#8BC34A', // 1-3 hours (light green)
      '#FFEB3B', // 3-6 hours (yellow)
      '#FFA000', // 6-12 hours (amber)
      '#E64A19', // 12-24 hours (deep orange)
      '#B71C1C', // > 24 hours (dark red)
    ],
  };

  // Line chart data
  public performanceTrends = [];

  // Mock data for the detailed metrics table
  public detailedMetrics: any = [];

  // Columns to display in the table
  public displayedColumns: string[] = [
    'courier',
    'total',
    'onTime',
    'onTimePercent',
    'avgDelay',
    'gradeA',
    'gradeB',
    'gradeC',
    'gradeD',
    'gradeF',
    'gService',
  ];

  // Columns for claims-enabled table
  public claimsEnabledColumns: string[] = [
    'courier',
    'total',
    'onTime',
    'onTimePercent',
    'avgDelay',
    'claims',
    'claimRate',
    'lateDeliveryClaims',
    'gService',
  ];

  // Courier-specific shipments table
  public courierShipments: any[] = [];
  public shipmentColumns: string[] = [
    'accountType',
    'courier',
    'trackingNumber',
    'hasClaim',
    'shipmentCreatedAt',
    'estimatedDelivery',
    'pickupTime',
    'expectedDelivery',
    'actualDelivery',
    'delay',
    'performanceGrade',
    'isGuaranteed',
    'serviceName',
    'originCity',
    'originProvince',
    'destinationCity',
    'destinationProvince',
  ];
  // Shipments columns with claims
  public shipmentsClaimsColumns: string[] = [
    'accountType',
    'courier',
    'trackingNumber',
    'hasClaim',
    'shipmentCreatedAt',
    'estimatedDelivery',
    'pickupTime',
    'expectedDelivery',
    'actualDelivery',
    'delay',
    'performanceGrade',
    'isGuaranteed',
    'serviceName',
    'originCity',
    'originProvince',
    'destinationCity',
    'destinationProvince',
  ];

  public currentShipmentsPage: number = 0;
  public shipmentsPerPage: number = 10;
  public totalShipmentsCount: number = 0;

  public isExporting: boolean = false;
  public exportJobId: string = null;
  public exportProgress: number = 0;
  public exportStatus: 'waiting' | 'active' | 'completed' | 'failed' = null;
  public exportDownloadUrl: string = null;
  private exportStatusInterval: any = null;

  // Toggle for showing claim stats
  public showClaimsView: boolean = false;

  // Sorting properties
  public sort: MatSort;
  public sortedData: any[] = [];

  constructor(
    private helpersService: HelpersService,
    private otpService: OtpService,
    private logger: LoggerService,
    private formBuilder: FormBuilder,
    private bannerNotificationsService: BannerNotificationsService,
    private userService: UserService,
    private stateService: StateService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loading = true;
    this.adminUserInfo$.subscribe((adminUser: AdminUser) => {
      this.canViewOtp = this.userService.hasAuthScope(adminUser, [AdminScopes.VIEW_OTP]);
      if (this.canViewOtp) {
        this.user = adminUser;
        this.timeBucket$.subscribe((timeBucketInfo: TimeBucketInfoInterface | null): void => {
          this.currentBucket = timeBucketInfo.timeBucket;
          this.currentHistory = timeBucketInfo.history || 1;
          this.accountType = timeBucketInfo.accountType;
          this.getCourierData();
        });
        this.courierFilter$.subscribe((courierFilter: ShippingProviders[] | null): void => {
          if (this.couriersDropdown.length === 0) {
            this.selectedCouriers = courierFilter;
            this.createForm();
          }
          this.courierFilter = courierFilter;
          this.loadCourierShipments();
          this.getCourierData();
        });
      }
    });
  }

  ngOnDestroy() {
    this.stopPollingExportStatus();
    this.adminUserInfoSubscription?.unsubscribe();
    this.timeBucketSubscription?.unsubscribe();
    this.courierFilterSubscription?.unsubscribe();
  }

  public pageChanged(pageEvent: PageEvent): void {
    this.itemsPerPage = pageEvent.pageSize;
    this.currentPage = pageEvent.pageIndex;
    const end = this.itemsPerPage * (this.currentPage + 1);
    const start = end - this.itemsPerPage;
  }

  public showAllServices(): void {
    this.navigateToServicesView();
  }

  private getCourierData() {
    this.loading = true;
    const params = {
      timeBucket: this.currentBucket,
      history: this.currentHistory,
      accountType: this.accountType,
    };
    if (this.courierFilter?.length > 0) {
      _.extend(params, { couriers: this.courierFilter });
    }
    this.otpService.getCouriers(params).subscribe(
      (response) => {
        this.logger.log('GET Otp Couriers', response);
        this.bucketData = response;
        this.setupMetrics();
        this.setupClaimsData();
        this.prepareCourierDelayDistribution();
      },
      (error) => {
        this.logger.error('GET Otp Couriers error', error);
      }
    );
  }

  private prepareCourierDelayDistribution(): void {
    const metricsData = this.bucketData?.metrics || [];
    if (metricsData.length === 0) return;

    // Reset both data arrays
    this.courierDelayDistribution = [];
    this.courierDelayPercentageData = [];

    metricsData.forEach((courier) => {
      const latestData = courier.data[courier.data.length - 1];
      if (!latestData.delayDistribution) return;

      const courierName = this.helpersService.convertShipmentProviderToString(courier.courier);

      // Raw counts
      const seriesData = [
        { name: '< 1 hour', value: latestData.delayDistribution.within_1hr },
        { name: '1-3 hours', value: latestData.delayDistribution.within_3hr },
        { name: '3-6 hours', value: latestData.delayDistribution.within_6hr },
        { name: '6-12 hours', value: latestData.delayDistribution.within_12hr },
        { name: '12-24 hours', value: latestData.delayDistribution.within_24hr },
        { name: '> 24 hours', value: latestData.delayDistribution.over_24hr },
      ];

      this.courierDelayDistribution.push({
        name: courierName,
        series: seriesData,
        courierCode: courier.courier, // Store code for reference
      });

      // Calculate percentages
      const total = seriesData.reduce((sum, item) => sum + item.value, 0);
      const percentageSeries = seriesData.map((item) => ({
        name: item.name,
        value: total > 0 ? (item.value / total) * 100 : 0,
      }));

      this.courierDelayPercentageData.push({
        name: courierName,
        series: percentageSeries,
        courierCode: courier.courier, // Store code for reference
      });
    });

    // Sort courier delay distribution by total volume (highest to lowest)
    this.courierDelayDistribution.sort((a, b) => {
      const aTotal = a.series.reduce((sum, item) => sum + item.value, 0);
      const bTotal = b.series.reduce((sum, item) => sum + item.value, 0);
      return bTotal - aTotal;
    });

    // Sort courier delay percentage data by total volume (highest to lowest)
    this.courierDelayPercentageData.sort((a, b) => {
      const aTotal = a.series.reduce((sum, item) => sum + item.value, 0);
      const bTotal = b.series.reduce((sum, item) => sum + item.value, 0);
      return bTotal - aTotal;
    });

    if (this.courierFilter.length === 1) {
      this.selectCourier(this.courierFilter[0]);
    }
  }

  // Toggle handler
  public onViewToggleChange(isPercentage: boolean): void {
    this.showAsPercentage = isPercentage;
  }

  // Update courier selection to pre-calculate trend data
  public selectCourier(courierCode: string): void {
    this.selectedCourier = courierCode;
    this.selectedCourierTrendData = this.calculateCourierDelayTrend(courierCode);
  }

  // Renamed to calculateCourierDelayTrend - now only called once when courier is selected
  private calculateCourierDelayTrend(courierCode: string): any[] {
    const courierData = this.bucketData?.metrics?.find((c) => c.courier === courierCode);

    if (!courierData) return [];

    // Create series for each delay category
    return [
      {
        name: '< 1 hour',
        series: courierData.data.map((d) => ({
          name: d.timeBucket,
          value: d.delayDistribution?.within_1hr || 0,
        })),
      },
      {
        name: '1-3 hours',
        series: courierData.data.map((d) => ({
          name: d.timeBucket,
          value: d.delayDistribution?.within_3hr || 0,
        })),
      },
      {
        name: '3-6 hours',
        series: courierData.data.map((d) => ({
          name: d.timeBucket,
          value: d.delayDistribution?.within_6hr || 0,
        })),
      },
      {
        name: '6-12 hours',
        series: courierData.data.map((d) => ({
          name: d.timeBucket,
          value: d.delayDistribution?.within_12hr || 0,
        })),
      },
      {
        name: '12-24 hours',
        series: courierData.data.map((d) => ({
          name: d.timeBucket,
          value: d.delayDistribution?.within_24hr || 0,
        })),
      },
      {
        name: '> 24 hours',
        series: courierData.data.map((d) => ({
          name: d.timeBucket,
          value: d.delayDistribution?.over_24hr || 0,
        })),
      },
    ];
  }

  private setupMetrics() {
    const gradeDistributionData = this.bucketData?.gradeDistribution;
    if (gradeDistributionData?.data?.length > 0) {
      const latestGradeData = gradeDistributionData?.data[gradeDistributionData?.data.length - 1];
      this.gradeDistribution[0].value = latestGradeData?.A || 0;
      this.gradeDistribution[1].value = latestGradeData?.B || 0;
      this.gradeDistribution[2].value = latestGradeData?.C || 0;
      this.gradeDistribution[3].value = latestGradeData?.D || 0;
      this.gradeDistribution[4].value = latestGradeData?.F || 0;
    }

    const metricsData = this.bucketData?.metrics;
    if (metricsData?.length > 0) {
      this.detailedMetrics = [];
      this.courierComparison = [];
      this.gTotalShipments = 0;
      this.gOnTimeCount = 0;
      this.gOnTimeRate = 0;
      this.gAverageDelay = 0;
      let services = [];

      metricsData.forEach((courier: any) => {
        const latestData = courier.data[courier.data.length - 1];

        const gradeA = latestData?.gradeDistribution.A || 0;
        const gradeB = latestData?.gradeDistribution.B || 0;
        const gradeC = latestData?.gradeDistribution.C || 0;
        const gradeD = latestData?.gradeDistribution.D || 0;
        const gradeF = latestData?.gradeDistribution.F || 0;
        const courierTotal = latestData?.totalShipments || 0;
        const gServiceTotal = latestData?.guaranteedService.total || 0;
        const gServiceOnTime = latestData?.guaranteedService.onTime || 0;

        this.gTotalShipments += gServiceTotal;
        this.gOnTimeCount += gServiceOnTime;
        this.gAverageDelay += latestData?.guaranteedService.averageDelay || 0;

        const serviceTypeBreakdown = latestData?.services;
        if (serviceTypeBreakdown && Object.keys(serviceTypeBreakdown).length > 0) {
          Object.keys(serviceTypeBreakdown).forEach((serviceCode) => {
            const serviceType = serviceTypeBreakdown[serviceCode];
            services.push({
              code: serviceCode,
              name: serviceType.serviceName,
              totalShipments: serviceType.totalShipments,
              onTimeShipments: serviceType.onTimeShipments,
              onTimePercentage: serviceType.onTimePercentage,
              averageDelay: serviceType.averageDelay,
            });
          });
        }

        const gService = (gServiceOnTime / gServiceTotal) * 100 || 0;
        const courierName = this.helpersService.convertShipmentProviderToString(courier.courier);
        const metrics = {
          courier: courierName,
          courierCode: courier.courier,
          total: courierTotal,
          onTime: latestData?.onTimeShipments || 0,
          onTimePercent: latestData?.onTimePercentage || 0,
          avgDelay: latestData?.averageDelay || 0,
          gradeA: gradeA / courierTotal || 0,
          gradeB: gradeB / courierTotal || 0,
          gradeC: gradeC / courierTotal || 0,
          gradeD: gradeD / courierTotal || 0,
          gradeF: gradeF / courierTotal || 0,
          gService,
        };

        this.detailedMetrics.push(metrics);
        this.courierComparison.push({
          name: courierName,
          value: courierTotal,
        });
      });

      // Sort courier comparison by volume (highest to lowest)
      this.courierComparison.sort((a, b) => b.value - a.value);

      this.serviceBreakdown = [];
      if (services.length > 0) {
        this.allServiceMetrics = this.helpersService.sortArray(services, 'totalShipments');
        this.allServiceMetrics.slice(0, 5).forEach((service) => {
          this.serviceBreakdown.push({
            name: service.name,
            value: service.totalShipments,
          });
        });
      }
      this.gOnTimeRate = (this.gOnTimeCount / this.gTotalShipments) * 100 || 0;
    }

    const totalsData = this.bucketData?.totals;
    if (totalsData.data?.length > 0) {
      const latestTotals = totalsData.data[totalsData.data.length - 1];
      this.totalShipments = latestTotals.totalShipments || 0;
      this.onTimeRate = latestTotals.onTimePercentage || 0;
      this.averageDelay = latestTotals.averageDelay || 0;
    }

    this.prepareGradeDistributionTrends();

    if (this.hasClaimsData) {
      this.displayedColumns = [
        'courier',
        'total',
        'onTime',
        'onTimePercent',
        'avgDelay',
        'claims',
        'claimRate',
        'lateDeliveryClaims',
        'gradeA',
        'gradeB',
        'gradeC',
        'gradeD',
        'gradeF',
        'gService',
      ];
    } else {
      this.displayedColumns = [
        'courier',
        'total',
        'onTime',
        'onTimePercent',
        'avgDelay',
        'gradeA',
        'gradeB',
        'gradeC',
        'gradeD',
        'gradeF',
        'gService',
      ];
    }

    // Initialize sorted data
    this.sortedData = [...this.detailedMetrics];

    this.couriersSetup = true;
    this.loading = false;
  }

  private setupClaimsData() {
    // Check if claims data is available in the response
    this.hasClaimsData = this.bucketData?.totals?.data?.some((item) => item.totalClaims > 0 || item.claimRate > 0);

    if (!this.hasClaimsData) {
      return;
    }

    // Set up overall claims data from totals
    const totalsData = this.bucketData?.totals?.data;
    if (totalsData?.length > 0) {
      const latestData = totalsData[totalsData.length - 1];

      if (latestData.totalClaims > 0) {
        this.totalClaims = latestData.totalClaims;
        this.overallClaimRate = latestData.claimRate || 0;
        this.claimRateTrend = this.bucketData?.totals?.trend?.claimRate || TrendDirection.STABLE;
      }

      // Set up claims trend data
      this.claimTrendData = totalsData.map((item) => ({
        name: item.timeBucket,
        value: item.claimRate || 0,
      }));
    }

    // Process claims data per courier
    const metricsData = this.bucketData?.metrics;
    if (metricsData?.length > 0) {
      // Create courierClaimRates chart data
      this.courierClaimRates = metricsData
        .map((courier) => {
          // Get the most recent data point
          const latestData = courier.data[courier.data.length - 1];

          // Check if claims data exists
          if (!latestData.claims) {
            return {
              name: this.helpersService.convertShipmentProviderToString(courier.courier),
              value: 0,
            };
          }

          return {
            name: this.helpersService.convertShipmentProviderToString(courier.courier),
            value: latestData.claims.claimRate || 0,
          };
        })
        .filter((item) => item.value > 0);

      // Sort by claim rate (highest first for visibility)
      this.courierClaimRates.sort((a, b) => b.value - a.value);

      // Create courier claims time series data
      this.courierClaimsData = metricsData
        .filter((courier) => courier.data.some((d) => d.claims && d.claims.claimRate > 0))
        .map((courier) => {
          return {
            name: this.helpersService.convertShipmentProviderToString(courier.courier),
            series: courier.data
              .filter((d) => d.claims)
              .map((d) => ({
                name: d.timeBucket,
                value: d.claims.claimRate || 0,
              })),
          };
        });

      // Update detailed metrics with claims info
      metricsData.forEach((courier) => {
        // Find this courier in detailedMetrics
        const courierMetric = this.detailedMetrics.find((m) => m.courierCode === courier.courier);

        if (!courierMetric) return;

        // Get latest data point
        const latestData = courier.data[courier.data.length - 1];

        // Add claims data if available
        if (latestData.claims) {
          courierMetric.totalClaims = latestData.claims.totalClaims || 0;
          courierMetric.claimRate = latestData.claims.claimRate || 0;

          // Add late delivery claims if available
          if (latestData.claims.claimsForLateDeliveries) {
            courierMetric.lateDeliveryClaims = latestData.claims.claimsForLateDeliveries.count || 0;
            courierMetric.lateDeliveryClaimRate =
              latestData.claims.claimsForLateDeliveries.percentageOfLateDeliveries || 0;
          }

          // Add grade distribution for claims if available
          if (latestData.claims.claimsByGrade) {
            courierMetric.claimsByGrade = latestData.claims.claimsByGrade;
          }
        }
      });

      // Create claims by grade data
      if (metricsData.some((courier) => courier.data.some((d) => d.claims && d.claims.claimsByGrade))) {
        this.claimsByGradeData = [
          { name: 'Grade A', value: 0 },
          { name: 'Grade B', value: 0 },
          { name: 'Grade C', value: 0 },
          { name: 'Grade D', value: 0 },
          { name: 'Grade F', value: 0 },
        ];

        // Sum up claims by grade across all couriers
        metricsData.forEach((courier) => {
          const latestData = courier.data[courier.data.length - 1];
          if (latestData.claims && latestData.claims.claimsByGrade) {
            if (latestData.claims.claimsByGrade.A)
              this.claimsByGradeData[0].value += latestData.claims.claimsByGrade.A.count || 0;
            if (latestData.claims.claimsByGrade.B)
              this.claimsByGradeData[1].value += latestData.claims.claimsByGrade.B.count || 0;
            if (latestData.claims.claimsByGrade.C)
              this.claimsByGradeData[2].value += latestData.claims.claimsByGrade.C.count || 0;
            if (latestData.claims.claimsByGrade.D)
              this.claimsByGradeData[3].value += latestData.claims.claimsByGrade.D.count || 0;
            if (latestData.claims.claimsByGrade.F)
              this.claimsByGradeData[4].value += latestData.claims.claimsByGrade.F.count || 0;
          }
        });
      }
    }

    // Update sorted data after claims data is processed
    this.sortedData = [...this.detailedMetrics];
  }

  private loadCourierShipments(): void {
    if (!this.currentBucket) return;

    this.otpService
      .getAggregateShipmentsCount(this.currentBucket, AggregationType.COURIER, { couriers: this.courierFilter })
      .subscribe(
        (response) => {
          this.totalShipmentsCount = response.count;
          this.fetchCourierShipments();
        },
        (error) => {
          this.logger.error('GET Shipments Count error', error);
        }
      );
  }

  private fetchCourierShipments(): void {
    const params = {
      page: this.currentShipmentsPage + 1,
      pageSize: this.shipmentsPerPage,
    };

    if (this.courierFilter?.length > 0) {
      _.extend(params, { couriers: this.courierFilter });
    }

    this.otpService.getAggregateShipments(this.currentBucket, AggregationType.COURIER, params).subscribe(
      (response: ShipmentAggregateResponse) => {
        this.logger.log('GET Courier Shipments', response);
        this.courierShipments = response.data.map((shipment) => {
          return {
            account_type: shipment.account_type,
            courier_name: this.helpersService.convertShipmentProviderToString(shipment.courier),
            tracking_number: shipment.tracking_number,
            shipment_created_at: shipment.shipment_created_at,
            pickup_time: shipment.pickup_time,
            expected_delivery: shipment.expected_delivery,
            actual_delivery: shipment.actual_delivery,
            estimated_delivery: shipment.estimated_delivery,
            origin_city: shipment.origin_city,
            origin_province: shipment.origin_province,
            destination_city: shipment.destination_city,
            destination_province: shipment.destination_province,
            performance_grade: shipment.performance_grade,
            service_name: shipment.service_name,
            is_guaranteed: shipment.is_guaranteed,
            delay: shipment.delay,
            has_claim: shipment.has_claim || false,
            claim_status: shipment.claim_status,
            claim_amount: shipment.claim_amount,
          };
        });
      },
      (error) => {
        this.logger.error('GET Courier Shipments error', error);
      }
    );
  }

  public shipmentPageChanged(pageEvent: PageEvent): void {
    this.shipmentsPerPage = pageEvent.pageSize;
    this.currentShipmentsPage = pageEvent.pageIndex;
    this.fetchCourierShipments();
  }

  public exportCourierShipments(): void {
    if (this.isExporting) return;

    this.isExporting = true;
    this.exportProgress = 0;
    this.exportStatus = 'waiting';
    this.exportDownloadUrl = null;

    const exportRequest: ExportJobRequest = {
      couriers: this.courierFilter,
      accountType: AccountTypesForOTP.ALL,
      user: {
        email: this.user?.email,
        firstName: this.user?.firstName,
        lastName: this.user?.lastName,
      },
    };

    this.otpService.exportAggregateShipments(this.currentBucket, AggregationType.COURIER, exportRequest).subscribe(
      (response: ExportJobResponse) => {
        this.logger.log('Export job created:', response);
        this.exportJobId = response.jobId;
        this.startPollingExportStatus();
      },
      (error) => {
        this.logger.error('Failed to create export job:', error);
        this.isExporting = false;
        // Show error message to user
        this.bannerNotificationsService.error('Failed to start export job.');
      }
    );
  }

  private startPollingExportStatus(): void {
    // Clear any existing interval
    if (this.exportStatusInterval) {
      clearInterval(this.exportStatusInterval);
    }

    // Check status every 2 seconds
    this.exportStatusInterval = setInterval(() => {
      this.checkExportStatus();
    }, 500);
  }

  private checkExportStatus(): void {
    if (!this.exportJobId) {
      this.stopPollingExportStatus();
      return;
    }

    this.otpService.getExportJobStatus(this.exportJobId).subscribe(
      (status: ExportJobStatus) => {
        this.exportStatus = status.status;
        this.exportProgress = status.progress;

        if (status.downloadUrl) {
          this.exportDownloadUrl = status.downloadUrl;
        }

        if (status.status === 'completed') {
          this.onExportCompleted(status);
        } else if (status.status === 'failed') {
          this.onExportFailed(status);
        }
      },
      (error) => {
        this.logger.error('Failed to get export status:', error);
        this.stopPollingExportStatus();
        this.isExporting = false;
        this.bannerNotificationsService.error('Failed to check export status.');
      }
    );
  }

  private onExportCompleted(status: ExportJobStatus): void {
    this.stopPollingExportStatus();
    this.isExporting = false;

    // Show success message
    const message = `Export completed with ${status.recordCount} records. The download will start automatically.`;
    this.bannerNotificationsService.success(message);

    // Automatically start download
    if (status.downloadUrl) {
      window.open(status.downloadUrl, '_blank');
    }
  }

  private onExportFailed(status: ExportJobStatus): void {
    this.stopPollingExportStatus();
    this.isExporting = false;

    // Show error message
    const message = `Export failed: ${status.error || 'Unknown error'}`;
    this.bannerNotificationsService.error(message);
  }

  private stopPollingExportStatus(): void {
    if (this.exportStatusInterval) {
      clearInterval(this.exportStatusInterval);
      this.exportStatusInterval = null;
    }
  }

  // Helper to format claim rate for display
  public formatClaimRate(rate: number): string {
    if (rate === undefined || rate === null) return 'N/A';
    return rate.toFixed(2) + '%';
  }

  // Helper to get color for claim rate
  public getClaimRateColor(rate: number): string {
    if (rate === undefined || rate === null) return '#6c757d'; // Gray for N/A
    if (rate === 0) return '#6c757d'; // Gray for zero
    if (rate < 0.5) return '#28a745'; // Green for low
    if (rate < 1.0) return '#ffc107'; // Yellow for medium
    return '#dc3545'; // Red for high
  }

  // Helper to get trend icon
  public getTrendIcon(trend: TrendDirection): string {
    switch (trend) {
      case TrendDirection.IMPROVING:
        return 'trending_up';
      case TrendDirection.DECLINING:
        return 'trending_down';
      default:
        return 'trending_flat';
    }
  }

  // Helper to get trend class for claims (inverse metric - lower is better)
  public getClaimsTrendClass(trend: TrendDirection): string {
    switch (trend) {
      case TrendDirection.IMPROVING:
        return 'positive-trend';
      case TrendDirection.DECLINING:
        return 'negative-trend';
      default:
        return 'stable-trend';
    }
  }

  private createForm() {
    this.form = this.formBuilder.group({
      couriers: ['', []],
    });
    const couriers = this.otpService.getSupportedCouriers();
    couriers.forEach((courier: any) => {
      this.couriersDropdown.push({
        id: courier.id,
        text: courier.name,
        selected: this.selectedCouriers?.includes(courier.id) || false,
      });
    });
    if (this.selectedCouriers) {
      this.form.patchValue(
        {
          couriers: this.selectedCouriers,
        },
        {
          emitEvent: false,
        }
      );
    }
    this.form.valueChanges.subscribe(() => {
      const couriers = this.form.value.couriers;
      this.stateService.setCourierFilterData(couriers);

      if (couriers.length === 1) {
        this.selectCourier(couriers[0]);
      } else {
        this.selectedCourier = null;
        this.selectedCourierTrendData = [];
      }
    });
  }

  private prepareGradeDistributionTrends(): void {
    // Reset the trends data
    this.gradeDistributionTrends = [];

    // Get grade distribution data from the bucketData
    const gradeData = this.bucketData?.gradeDistribution?.data;

    if (!gradeData || gradeData.length === 0) return;

    // Create series for each grade
    const gradeASeries = { name: 'Grade A', series: [] };
    const gradeBSeries = { name: 'Grade B', series: [] };
    const gradeCSeries = { name: 'Grade C', series: [] };
    const gradeDSeries = { name: 'Grade D', series: [] };
    const gradeFSeries = { name: 'Grade F', series: [] };

    // Populate series with data points
    gradeData.forEach((item) => {
      gradeASeries.series.push({
        name: item.timeBucket,
        value: item.A || 0,
      });

      gradeBSeries.series.push({
        name: item.timeBucket,
        value: item.B || 0,
      });

      gradeCSeries.series.push({
        name: item.timeBucket,
        value: item.C || 0,
      });

      gradeDSeries.series.push({
        name: item.timeBucket,
        value: item.D || 0,
      });

      gradeFSeries.series.push({
        name: item.timeBucket,
        value: item.F || 0,
      });
    });

    // Add all series to the trends data
    this.gradeDistributionTrends = [gradeASeries, gradeBSeries, gradeCSeries, gradeDSeries, gradeFSeries];
  }

  public formatYAxisNumberTicks(val: any): string {
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return val;
  }

  public navigateToServicesView(): void {
    this.router.navigate(['otp', 'details', 'services']);
  }

  // Sorting method for the detailed metrics table
  public sortData(sort: Sort): void {
    const data = this.detailedMetrics.slice();
    if (!sort.active || sort.direction === '') {
      this.sortedData = data;
      return;
    }

    this.sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'total':
          return this.helpersService.compare(a.total, b.total, isAsc);
        case 'onTime':
          return this.helpersService.compare(a.onTime, b.onTime, isAsc);
        case 'onTimePercent':
          return this.helpersService.compare(a.onTimePercent, b.onTimePercent, isAsc);
        case 'avgDelay':
          return this.helpersService.compare(a.avgDelay, b.avgDelay, isAsc);
        default:
          return 0;
      }
    });
  }
}
